import styled from 'styled-components';
import { Button } from 'antd';
import { Link } from 'react-router-dom';

import { breakpoints, fontWeights, palette } from 'theme/variables';

/* ══════════════════════════════════════════════════════
   Login — Option B v4
   Bottle green brand panel · forest CTA · sage highlight
   ══════════════════════════════════════════════════════ */

export const LoginPage = styled.div`
    display: flex;
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
    background: ${palette.warmBg};
`;

/* ── left side — brand ───────────────────────────────────── */

export const BrandSide = styled.div`
    display: none;

    ${breakpoints.medium} {
        display: flex;
        flex-direction: column;
        width: 42%;
        min-width: 320px;
        background: ${palette.bottle};
        padding: 3rem 2.5rem;
        position: relative;
        overflow: hidden;

        /* gradient right-rule (forest → lime → gold) */
        &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 5px;
            background: linear-gradient(
                to bottom,
                ${palette.forest} 0%,
                ${palette.lime} 55%,
                ${palette.gold} 100%
            );
        }

        /* radial glow bottom-right */
        &::after {
            content: '';
            position: absolute;
            bottom: -60px;
            right: -60px;
            width: 280px;
            height: 280px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(30, 122, 69, 0.18) 0%, transparent 70%);
            pointer-events: none;
        }
    }

    ${breakpoints.desktop} {
        width: 44%;
        min-width: 440px;
        padding: 4rem 3.5rem;
    }
`;

export const BrandLogo = styled.img`
    width: 200px;
    filter: brightness(0) invert(1);
    position: relative;
    z-index: 1;

    ${breakpoints.desktop} {
        width: 240px;
    }
`;

export const BrandContent = styled.div`
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2rem 0;
`;

export const BrandTitle = styled.h1`
    color: #fff;
    font-family: 'Lato', 'Inter', sans-serif;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.18;
    margin: 0 0 1rem;
    letter-spacing: -0.4px;

    em {
        color: ${palette.sage};
        font-style: normal;
    }

    ${breakpoints.desktop} {
        font-size: 2.4rem;
    }
`;

export const BrandDescription = styled.p`
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Lato', 'Inter', sans-serif;
    font-weight: 300;
    font-size: 0.95rem;
    line-height: 1.7;
    margin: 0;
    max-width: 380px;
`;

export const BrandFooter = styled.div`
    position: relative;
    z-index: 1;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

export const BrandFooterLabel = styled.span`
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.35);
    margin-bottom: 0.6rem;
`;

export const BrandPills = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

export const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.55);
    font-size: 11px;
    font-family: 'Inter', sans-serif;
`;

/* ── right side — form ───────────────────────────────────── */

export const FormSide = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 1.5rem 1rem;
    background: #fff;

    ${breakpoints.small} {
        padding: 2rem 1.5rem;
    }

    ${breakpoints.medium} {
        padding: 3rem;
    }
`;

export const FormWrap = styled.div`
    width: 100%;
    max-width: 420px;
`;

export const FormTitle = styled.h2`
    font-family: 'Lato', 'Inter', sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    color: ${palette.canopy};
    margin: 0 0 0.25rem;
    letter-spacing: -0.2px;
`;

export const FormSubtitle = styled.p`
    font-size: 0.875rem;
    color: ${palette.warm400};
    margin: 0 0 1.75rem;
`;

/* ── form fields ─────────────────────────────────────────── */

export const StyledFormWrapper = styled.div`
    width: 100%;

    .ant-legacy-form-item,
    .ant-form-item {
        margin-bottom: 1.25rem;
    }

    .ant-input-affix-wrapper {
        border-radius: 8px;
        border: 1.5px solid ${palette.warm200};
        padding: 10px 12px;
        background: #fff;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:hover {
            border-color: ${palette.warm400};
        }

        &:focus-within {
            border-color: ${palette.forest};
            box-shadow: 0 0 0 3px rgba(30, 122, 69, 0.1);
        }

        .ant-input {
            background: transparent;
            font-size: 0.9rem;
            font-family: 'Inter', sans-serif;
            color: ${palette.canopy};

            &::placeholder {
                color: ${palette.warm400};
            }
        }
    }

    .anticon {
        color: ${palette.warm400};
        font-size: 0.9rem;
        transition: color 0.2s;
    }

    .ant-input-affix-wrapper:focus-within .anticon {
        color: ${palette.forest};
    }
`;

export const FieldLabel = styled.label`
    display: block;
    font-size: 0.8rem;
    font-weight: 700;
    color: ${palette.canopy};
    margin-bottom: 6px;
`;

export const LoginButton = styled(Button)`
    && {
        width: 100%;
        height: 44px;
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: ${fontWeights.bold};
        font-family: 'Inter', sans-serif;
        background: ${palette.forest};
        border-color: ${palette.forest};
        color: #fff;
        transition: all 0.2s ease;
        margin-top: 0.25rem;

        &:hover,
        &:focus {
            background: ${palette.fern};
            border-color: ${palette.fern};
            box-shadow: 0 4px 12px rgba(30, 122, 69, 0.3);
            transform: translateY(-1px);
            color: #fff;
        }

        &:active {
            background: #176338;
            border-color: #176338;
            transform: translateY(0);
            box-shadow: none;
        }
    }
`;

export const ForgotLink = styled(Link)`
    display: inline-block;
    margin-top: 1rem;
    font-size: 0.82rem;
    color: ${palette.forest};
    font-weight: ${fontWeights.medium};
    transition: color 0.2s;

    &:hover {
        color: ${palette.fern};
        text-decoration: underline;
    }
`;

export const FormFooter = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 1rem;
`;
